import {useEffect, useRef, useState} from "react";
import {useParams} from "react-router-dom";
import {useAppContext} from "../../../../contexts/AppContext";
import {useConfirm} from "../../../../contexts/ConfirmContext";
import baseUrl from "../../../../baseUrl";
import {requester} from "../../../../services/requester";
import {DealsCopyPlan} from "./DealsCopyPlan";

export const CopyFromPreviousYear = ({onDealSubmit, onDrawingSubmit, onOthersSubmit}) => {
    const {year, settlement_ekatte, bullstat} = useParams();
    const {companies} = useAppContext();
    const [sourceBullstat, setSourceBullstat] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    // У2: планът за замените — показва се преди копирането и се одобрява ред
    // по ред. Докато е зареден, панелът стои на екрана.
    const [dealsPlan, setDealsPlan] = useState(null);
    // Х2 (26.08.2026): щом планът се появи, екранът отива при него. Досега
    // списъкът се рисуваше под бутоните и при по-висок екран оставаше извън
    // полезрението — човекът натискаше и не виждаше нищо да се е случило.
    const планРеф = useRef(null);
    const request = requester();
    const confirm = useConfirm();
    const prevYear = Number(year) - 1;

    // Т3 — копирането наслагва. Когато в годината вече има данни, сървърът
    // първо връща въпрос вместо да копира; тук той се показва и при съгласие
    // заявката се праща наново с `confirm`.
    //
    // Случаят, заради който е направено: миналата година едно и също лице е
    // било вписано като двама ползвателя, а тази година е един. Копира се
    // веднъж от единия булстат и втори път от другия — и двете влизат.
    const копирай = async (какво, път, презареди) => {
        setLoading(true);
        setResult(null);
        const тяло = sourceBullstat ? {source_bullstat: sourceBullstat} : {};
        const прати = (потвърдено) => request.post(
            `${baseUrl()}${път}`, потвърдено ? {...тяло, confirm: true} : тяло);
        try {
            let res;
            try {
                res = await прати(false);
            } catch (err) {
                if (!err || !err.needs_confirm) throw err;
                const съгласен = await confirm({
                    title: "Да се добавят ли към вече въведените?",
                    message: err.message,
                    confirmText: "Да, добави",
                    cancelText: "Не",
                    note: "Ако копирате два пъти от един и същ източник, "
                        + "редовете ще се удвоят.",
                });
                if (!съгласен) { setLoading(false); return; }
                res = await прати(true);
            }
            setResult({type: какво, ...res});
            презареди();
            onOthersSubmit && onOthersSubmit();
        } catch (err) {
            setResult({type: какво, error: (err && err.message) || 'Грешка при копиране'});
        }
        setLoading(false);
    };

    useEffect(() => {
        window.scroll({
            top: document.body.offsetHeight,
            left: 0,
            behavior: 'smooth',
        });
    }, []);

    // П3: копирането на изчертавания пипа и реда „други площи" (БП, пътища,
    // двете „Други", корекция) — затова се презарежда и той.
    const copyDrawings = () => копирай(
        'drawings', `/copy/drawings/${year}/${settlement_ekatte}/${bullstat}`,
        onDrawingSubmit);

    // У2 — при замените НЕ се копира на сляпо. Първо се изтегля планът (какво
    // вече е отразено и какво не), човекът маха или добавя отметки и чак тогава
    // тръгва самото копиране. Затова тук не се ползва `копирай`.
    const showDealsPlan = async () => {
        setLoading(true);
        setResult(null);
        setDealsPlan(null);
        try {
            const заявка = `/copy/deals/${year}/${settlement_ekatte}/${bullstat}/preview`
                + (sourceBullstat ? `?source_bullstat=${encodeURIComponent(sourceBullstat)}` : '');
            const план = await request.get(`${baseUrl()}${заявка}`);
            if (план.items && план.items.length > 0) {
                setDealsPlan(план);
                // изчаква рисуването на панела, преди да превърти до него
                setTimeout(() => планРеф.current?.scrollIntoView(
                    {behavior: 'smooth', block: 'start'}), 50);
            } else {
                setResult({type: 'deals', copied: 0, message: план.message
                    || `Няма замени за ${prevYear} година.`});
            }
        } catch (err) {
            setResult({type: 'deals', error: (err && err.message) || 'Грешка при копиране'});
        }
        setLoading(false);
    };

    const copySelectedDeals = async (deal_ids) => {
        setLoading(true);
        try {
            const тяло = {deal_ids};
            if (sourceBullstat) тяло.source_bullstat = sourceBullstat;
            const res = await request.post(
                `${baseUrl()}/copy/deals/${year}/${settlement_ekatte}/${bullstat}`, тяло);
            setResult({type: 'deals', ...res});
            setDealsPlan(null);
            onDealSubmit();
        } catch (err) {
            setResult({type: 'deals', error: (err && err.message) || 'Грешка при копиране'});
        }
        setLoading(false);
    };

    const companies_options = companies.map(c => ({
        bullstat: c.bullstat,
        label: `${c.company_name}, ${c.bullstat}`
    })).filter(c => String(c.bullstat) !== String(bullstat));

    return (
        <div>
            <h6 className="mt-2">Копиране от {prevYear} година</h6>

            <div className="row g-3 align-items-center mb-3">
                <div className="col-auto">
                    <label className="col-form-label">Източник (ако е различен ползвател):</label>
                </div>
                <div className="col-4">
                    <input
                        className="form-control"
                        type="search"
                        list="source-bullstat-list"
                        placeholder="Оставете празно за текущия"
                        value={sourceBullstat}
                        onChange={(e) => setSourceBullstat(e.target.value)}
                    />
                    <datalist id="source-bullstat-list">
                        {companies_options.map(c => (
                            <option key={c.bullstat} value={c.bullstat}>{c.label}</option>
                        ))}
                    </datalist>
                </div>
            </div>

            <div className="d-flex gap-2 mb-3">
                <button
                    className="btn btn-outline-primary"
                    onClick={copyDrawings}
                    disabled={loading}
                >
                    {loading ? 'Копиране...' : `Копирай изчертавания от ${prevYear}`}
                </button>
                <button
                    className="btn btn-outline-primary"
                    onClick={showDealsPlan}
                    disabled={loading}
                >
                    {loading ? 'Изчисляване...' : `Копирай замени от ${prevYear}`}
                </button>
            </div>

            {dealsPlan && (
                <div ref={планРеф}>
                <DealsCopyPlan
                    plan={dealsPlan}
                    year={Number(year)}
                    loading={loading}
                    onCopy={copySelectedDeals}
                    onCancel={() => setDealsPlan(null)}
                />
                </div>
            )}

            {result && !result.error && (
                <div className={`alert ${result.copied > 0 ? 'alert-success' : 'alert-warning'}`}>
                    <strong>{result.message}</strong>
                    {result.skipped_existing > 0 && (
                        <div className="mt-2">
                            Прескочени като вече отразени: {result.skipped_existing}.
                        </div>
                    )}
                    {result.skipped > 0 && (
                        <div className="mt-2">
                            Пропуснати: {result.skipped} (липсващи ползватели за {year}:
                            {' '}{result.missing_companies.join(', ')})
                        </div>
                    )}
                </div>
            )}

            {result && result.error && (
                <div className="alert alert-danger">
                    {result.error}
                </div>
            )}
        </div>
    );
};